import React, { FC, useEffect, useState } from 'react';
import { Popup } from '@silverfort/sf-components';
import InsuranceReportContent from './InsuranceReportContent/InsuranceReportContent';
import InsuranceReportFooter from './InsuranceReportFooter';
import useInsuranceReportGeneration from '../api/mutations/useInsuranceReportGeneration';
import moment from 'moment/moment';
import useGetInsuranceRuleOptions from '../api/queries/useGetInsuranceRuleOptions';
import { CustomControl, OldPasswordPolicy, PolicyData } from '../api/types/InsuranceReport';

const OLD_PASSWORD_OPTIONS = {
    3: '3 Months',
    6: '6 Months',
    12: '1 year'
};

interface Props {
    open: boolean;
    onClose: () => void;
    refreshReportFilesData: () => void;
}

const InsuranceReportPopup: FC<Props> = ({ open, onClose, refreshReportFilesData }) => {
    const [emailServicePolicyData, setEmailServicePolicyData] = useState<PolicyData>({
        selected: false
    });
    const [vpnServicePolicyData, setVpnServicePolicyData] = useState<PolicyData>({
        selected: false
    });
    const [oldPasswordPolicy, setOldPasswordPolicy] = useState<OldPasswordPolicy>({
        selected: false,
        value: Object.keys(OLD_PASSWORD_OPTIONS)[0]
    });
    const [isCompanyLogoUsed, setIsCompanyLogoUsed] = useState(false);
    const [customControls, setCustomControls] = useState<CustomControl[]>([{ name: '', policies: [] }]);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const {
        mutate: triggerInsuranceReportGeneration,
        isSuccessful: isInsuranceSuccessfullyCreated,
        isLoading: isGenerating
    } = useInsuranceReportGeneration(refreshReportFilesData);
    const { data } = useGetInsuranceRuleOptions();
    const insuranceRules = data ?? {
        options: [],
        optionsKeysToValues: {}
    };

    useEffect(() => {
        const validateData = () => {
            const errorMessages: string[] = [];
            if (emailServicePolicyData.selected && emailServicePolicyData.policies?.length === 0) {
                errorMessages.push('Email service is checked but policy is not selected');
            }
            if (vpnServicePolicyData.selected && vpnServicePolicyData.policies?.length === 0) {
                errorMessages.push('VPN service is checked but policy is not selected');
            }

            let isNoName = false;
            customControls.forEach((control) => {
                if (control.name && control.policies?.length === 0) {
                    errorMessages.push(`custom policy: ${control.name} policy is not selected`);
                } else if (!control.name) {
                    isNoName = true;
                }
            });
            if (isNoName) {
                errorMessages.push('custom policy: name is missing from one of the custom policies');
            }

            setValidationErrors(errorMessages);
        };
        validateData();
    }, [emailServicePolicyData, vpnServicePolicyData, customControls]);

    useEffect(() => {
        if (isInsuranceSuccessfullyCreated) {
            onClose();
        }
    }, [isInsuranceSuccessfullyCreated, onClose]);

    const onGenerate = async () => {
        const convertPoliciesNamesToIds = <T extends PolicyData>(policyData: T) => {
            return {
                ...policyData,
                policies: policyData.policies?.flatMap((name) => insuranceRules.optionsKeysToValues[name])
            };
        };

        const insuranceSettings = {
            startDate: moment().subtract(30, 'd'),
            endDate: moment(),
            emailPolicies: convertPoliciesNamesToIds(emailServicePolicyData),
            vpnPolicies: convertPoliciesNamesToIds(vpnServicePolicyData),
            oldPassword: oldPasswordPolicy,
            useCustomerIcon: isCompanyLogoUsed,
            customControls: customControls.map((control) => convertPoliciesNamesToIds(control))
        };

        await triggerInsuranceReportGeneration(insuranceSettings);
    };

    return (
        <Popup
            open={open}
            title="Generate Cyber Insurance Report"
            ContentComponent={InsuranceReportContent}
            ContentComponentProps={{
                emailServicePolicyData,
                setEmailServicePolicyData,
                vpnServicePolicyData,
                setVpnServicePolicyData,
                oldPasswordPolicy,
                setOldPasswordPolicy,
                isCompanyLogoUsed,
                setIsCompanyLogoUsed,
                customControls,
                setCustomControls,
                oldPasswordOptions: OLD_PASSWORD_OPTIONS,
                insuranceRules
            }}
            FooterComponent={InsuranceReportFooter}
            FooterComponentProps={{
                onGenerate,
                onClose,
                errors: validationErrors,
                isGenerating
            }}
            onClose={onClose}
            popupClassName="w-[720px] h-[616px] max-w-full"
        />
    );
};

export default InsuranceReportPopup;
